import axios from "axios";
import httpService from "../../../../services/httpService";
import {
  QuestionItem,
  UploadTrackerItem,
} from "../interfaces/QuestionBankInterfaces";

const QuestionBankClient = {
  // Download Template Presigned URL
  downloadTemplate: async (
    questionType: string = "MCQ",
    fileType: string = "excel",
  ) => {
    const response = await httpService.get("/admin/upload_questions/template", {
      params: { question_type: questionType, file_type: fileType },
    });
    return response.data;
  },

  // Bulk Upload File
  uploadQuestionsFile: async (formData: FormData) => {
    const response = await httpService.post(
      "/admin/upload-questions",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data;
  },

  // Upload Tracking History
  getUploadStatusView: async (
    fileId?: string,
    page: number = 1,
    limit: number = 10,
  ) => {
    const response = await httpService.get("/admin/questions/status_view", {
      params: { file_id: fileId, page, limit },
    });
    return response.data;
  },

  // Get Questions contained within a specific uploaded file
  getFileUploadQuestions: async (
    fileId: string,
    page: number = 1,
    limit: number = 10,
    search?: string,
  ) => {
    const response = await httpService.get(
      `/admin/questions/status_view/${fileId}/questions`,
      {
        params: { page, limit, search },
      },
    );
    return response.data;
  },

  // Master Question List
  listQuestions: async (params?: {
    module_id?: string;
    question_type?: string;
    difficulty_level?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const response = await httpService.get("/admin/questions", { params });
    return response.data;
  },

  // Single Question Details by ID
  getQuestion: async (questionId: string) => {
    const response = await httpService.get(`/admin/questions/${questionId}`);
    return response.data;
  },

  // Single Question Manual Create
  createQuestion: async (data: Partial<QuestionItem>) => {
    const response = await httpService.post("/admin/questions", data);
    return response.data;
  },

  // Single Question Update
  updateQuestion: async (questionId: string, data: Partial<QuestionItem>) => {
    const response = await httpService.post(
      `/admin/questions/${questionId}`,
      data,
    );
    return response.data;
  },

  // Delete Question
  deleteQuestion: async (questionId: string, moduleId?: string) => {
    const response = await httpService.delete(
      `/admin/questions/${questionId}`,
      {
        params: { module_id: moduleId },
      },
    );
    return response.data;
  },

  // Bulk Delete Questions (Module-based only)
  bulkDeleteQuestions: async (questionIds: string[], moduleId: string) => {
    const response = await httpService.post(`/admin/questions/bulk-delete`, {
      question_ids: questionIds,
      module_id: moduleId,
    });
    return response.data;
  },

  // Upload Question Image Asset
  uploadQuestionImage: async (formData: FormData) => {
    const response = await httpService.post(
      "/admin/upload/question_image",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
      },
    );
    return response.data;
  },

  // Modules List from DB
  listModules: async () => {
    const response = await httpService.get("/admin/modules");
    return response.data;
  },

  // Create Module in DB
  createModule: async (moduleName: string, description?: string) => {
    const response = await httpService.post("/admin/modules", {
      module_name: moduleName,
      description,
    });
    return response.data;
  },

  // Get S3 Presigned Upload URL
  createUploadUrl: async (filename: string) => {
    const response = await httpService.get("/admin/create_upload_url", {
      params: { filename },
    });
    return response.data;
  },

  // Upload file blob directly to S3 URL
  uploadFileToS3: async (uploadUrl: string, file: File) => {
    await axios.put(uploadUrl, file, {
      headers: {
        "Content-Type": file.type || "application/octet-stream",
      },
    });
  },

  // Generate AI Questions
  generateAIQuestions: async (data: {
    module_id: string;
    module_name?: string;
    context_type: string;
    user_prompt?: string;
    context_text?: string;
    context_file_name?: string;
    context_file?: string;
    no_of_questions_requested: number;
  }) => {
    const response = await httpService.post(
      "/admin/question-generation/generate",
      data,
    );
    return response.data;
  },

  // List AI Generation Requests
  listAIGenerationRequests: async (
    moduleId?: string,
    pageNumber: number = 1,
    limit: number = 10,
  ) => {
    const params: any = { page_number: pageNumber, limit };
    if (moduleId && moduleId !== "All") {
      params.module_id = moduleId;
    }
    const response = await httpService.get(
      "/admin/question-generation/requests",
      { params },
    );
    return response.data;
  },

  // Get AI Generation Request Details & Generated Questions
  getAIGenerationRequestDetails: async (
    requestId: string,
    moduleId: string,
  ) => {
    const response = await httpService.get(
      `/admin/question-generation/request/${requestId}`,
      {
        params: { module_id: moduleId },
      },
    );
    return response.data;
  },

  // Update Generated Question Status (Approve / Reject)
  updateAIGeneratedQuestionStatus: async (data: {
    module_id: string;
    request_id: string;
    question_id: string;
    status: "Approved" | "Rejected";
  }) => {
    const response = await httpService.post(
      "/admin/question-generation/status",
      data,
    );
    return response.data;
  },

  // Approve All Pending Questions for a Request
  approveAllAIGeneratedQuestions: async (
    requestId: string,
    moduleId: string,
  ) => {
    const response = await httpService.post(
      `/admin/question-generation/status?status=Approve%20all&module_id=${moduleId}&request_id=${requestId}`,
    );
    return response.data;
  },

  // Reject All Pending Questions for a Request
  rejectAllAIGeneratedQuestions: async (
    requestId: string,
    moduleId: string,
  ) => {
    const response = await httpService.post(
      `/admin/question-generation/status?status=Reject%20all&module_id=${moduleId}&request_id=${requestId}`,
    );
    return response.data;
  },

  // Rephrase Generated Question using AI
  rephraseAIGeneratedQuestion: async (
    questionId: string,
    requestId: string,
    moduleId: string,
    contextText: string,
  ) => {
    const response = await httpService.post(
      `/admin/question-generation/rephrase/${questionId}?request_id=${requestId}&module_id=${moduleId}`,
      { context_text: contextText },
    );
    return response.data;
  },

  // Edit AI Generated Question
  editAIGeneratedQuestion: async (
    questionId: string,
    requestId: string,
    moduleId: string,
    questionData: {
      question?: string;
      explanation?: string;
      difficulty_level?: string;
      concepts?: string[];
      answers?: Array<{
        answer_id?: string;
        answer: string;
        correct_answer: boolean;
      }>;
    },
  ) => {
    const response = await httpService.post(
      `/admin/question-generation/question/${questionId}?request_id=${requestId}&module_id=${moduleId}`,
      questionData,
    );
    return response.data;
  },
};

export default QuestionBankClient;
